import { type ComponentPropsWithoutRef, type Ref } from "react";

import { clsx } from "clsx";

import styles from "./RVFlexbox.module.scss";

/**
 * Props for `RVBox`.
 */
export interface BoxProps extends ComponentPropsWithoutRef<"div"> {
    /** Uniform padding spacing token */
    padding?: number;
    /** Top padding spacing token */
    paddingTop?: number;
    /** Right padding spacing token */
    paddingRight?: number;
    /** Bottom padding spacing token */
    paddingBottom?: number;
    /** Left padding spacing token */
    paddingLeft?: number;
    /** Additional className applied to the wrapper element */
    className?: string;
    /** CSS flex shorthand value */
    flex?: string;
    /** When true, applies full-width behavior for row layouts */
    fullWidth?: boolean;
    /** Optional forwarded ref to the root div */
    elementRef?: Ref<HTMLDivElement>;
}

/**
 * Props for `RVRow` and `RVColumn`.
 */
export interface FlexProps extends BoxProps {
    /** CSS justify-content value */
    justifyContent?: string;
    /** CSS align-items value */
    alignItems?: string;
    /** Gap spacing token between children */
    gap?: number;
}

function paramToClass(param: number | undefined, classPrefix: string) {
    return (
        param && {
            [styles[`${classPrefix}-${param}`]]: param,
        }
    );
}

/**
 * Box layout primitive with token-based padding helpers.
 *
 * @param props Box props controlling padding, flex, and base DOM attributes.
 * @returns A div wrapper with Regal spacing utility classes.
 */
export function RVBox(props: BoxProps): JSX.Element {
    const {
        padding,
        paddingTop,
        paddingRight,
        paddingBottom,
        paddingLeft,
        flex,
        className,
        children,
        style,
        elementRef,
        ...rest
    } = props;
    const paddingCls = paramToClass(padding, "box-padding");
    const paddingTopCls = paramToClass(paddingTop, "box-padding-top");
    const paddingRightCls = paramToClass(paddingRight, "box-padding-right");
    const paddingBottomCls = paramToClass(paddingBottom, "box-padding-bottom");
    const paddingLeftCls = paramToClass(paddingLeft, "box-padding-left");

    return (
        <div
            className={clsx(className, paddingCls, paddingTopCls, paddingRightCls, paddingBottomCls, paddingLeftCls)}
            style={{ ...style, flex }}
            ref={elementRef}
            {...rest}
        >
            {children}
        </div>
    );
}

/**
 * Horizontal flex layout helper.
 *
 * @param props Flex props for row alignment and spacing.
 * @returns `RVBox` configured as a row.
 */
export function RVRow(props: FlexProps): JSX.Element {
    const { className, gap, justifyContent, alignItems, style, ...rest } = props;
    const gapCls = paramToClass(gap, "flex-gap");
    return (
        <RVBox
            className={clsx("flex-row", gapCls, className, {
                ["full-width"]: props.fullWidth,
            })}
            style={{ ...style, alignItems, justifyContent }}
            {...rest}
        />
    );
}

/**
 * Vertical flex layout helper.
 *
 * @param props Flex props for column alignment and spacing.
 * @returns `RVBox` configured as a column.
 */
export function RVColumn(props: FlexProps): JSX.Element {
    const { className, gap, justifyContent, alignItems, style, ...rest } = props;
    const gapCls = paramToClass(gap, "flex-gap");

    return (
        <RVBox
            className={clsx("flex-column", gapCls, className)}
            style={{ ...style, alignItems, justifyContent }}
            {...rest}
        />
    );
}
